import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, LineChart, Line,
} from 'recharts';
import {
  Smartphone, Tablet, Watch, Radio, AlertTriangle, XCircle, Pencil, Building2, FileText, AlertCircle, Box
} from 'lucide-react';
import { QuoteState, MultiCarrierResult, CarrierBreakdown, deviceMrcCalc, deviceOneTimeCalc } from '../types';

interface Props {
  quote:                  QuoteState;
  currentSolution:        MultiCarrierResult;
  onUpdateCurrentSolution:(s: MultiCarrierResult) => void;
}

const Tile: React.FC<{ label: string; value: string; sub?: string; color?: string }> = ({
  label, value, sub, color = 'text-white',
}) => (
  <div className="p-5 rounded-2xl bg-white/5 border border-white/8">
    <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider">{label}</div>
    <div className={`text-2xl font-black ${color}`}>{value}</div>
    {sub && <div className="text-xs text-gray-600 mt-1">{sub}</div>}
  </div>
);

const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: { name: string; value: number; color: string }[]; label?: string }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1A1A2E] border border-white/10 rounded-xl p-3 shadow-2xl">
      <p className="text-xs font-semibold text-gray-400 mb-2">{label}</p>
      {payload.map(p => (
        <div key={p.name} className="flex items-center gap-2 text-sm">
          <div className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-gray-400">{p.name}:</span>
          <span className="font-bold text-white">${p.value.toFixed(2)}</span>
        </div>
      ))}
    </div>
  );
};

export const ComparisonDashboard: React.FC<Props> = ({
  quote, currentSolution, onUpdateCurrentSolution,
}) => {
  const [draftCarriers, setDraftCarriers] = useState<CarrierBreakdown[]>(currentSolution.carriers);
  const [editingIndex, setEditingIndex]   = useState<number | null>(null);

  // Sync draft if currentSolution changes from outside
  useEffect(() => {
    setDraftCarriers(currentSolution.carriers);
  }, [currentSolution]);

  // Derive combined totals dynamically from draft
  const draftCombined = draftCarriers.reduce((acc, c) => {
    if (c.failed) return acc; // Exclude failed extractions from totals
    return {
      monthlyServiceTotal: acc.monthlyServiceTotal + c.monthlyServiceTotal,
      monthlyHardwareTotal: acc.monthlyHardwareTotal + c.monthlyHardwareTotal,
      taxesAndFees: acc.taxesAndFees + c.taxesAndFees,
      etfTotal: acc.etfTotal + c.etfTotal,
      totalDue: acc.totalDue + c.totalDue,
      deviceCounts: {
        phone: acc.deviceCounts.phone + c.deviceCounts.phone,
        tablet: acc.deviceCounts.tablet + c.deviceCounts.tablet,
        watch: acc.deviceCounts.watch + c.deviceCounts.watch,
        connected: acc.deviceCounts.connected + c.deviceCounts.connected,
        other: acc.deviceCounts.other + c.deviceCounts.other,
      }
    };
  }, {
    monthlyServiceTotal: 0, monthlyHardwareTotal: 0, taxesAndFees: 0, etfTotal: 0, totalDue: 0,
    deviceCounts: { phone: 0, tablet: 0, watch: 0, connected: 0, other: 0 }
  });

  // Proposed solution service MRC
  const nxtServiceMrc = quote.items.reduce((s, i) => s + i.mrc * i.lines, 0);

  // Proposed solution device MRC
  const nxtDeviceMrc = quote.items.reduce(
    (s, i) => s + i.devices.reduce((d, dev) => d + deviceMrcCalc(dev), 0),
    0,
  );

  const nxtDeviceOneTime = quote.items.reduce(
    (s, i) => s + i.devices.reduce((d, dev) => d + deviceOneTimeCalc(dev), 0),
    0,
  );

  const nxtPromoDiscount = quote.promos
    .filter(p => p.type === 'monthly')
    .reduce((s, p) => s + p.value, 0);

  const nxtMrc = nxtServiceMrc + nxtDeviceMrc - nxtPromoDiscount;

  const currTotal   = draftCombined.totalDue;
  const savings     = currTotal - nxtMrc;
  const annualSave  = savings * 12;
  const termSave    = (savings * quote.term) - nxtDeviceOneTime;
  const roiMonths   = savings > 0 ? Math.ceil(draftCombined.etfTotal / savings) : 0;

  const barData = [
    {
      name: draftCarriers.length > 1 ? 'All Carriers' : (draftCarriers[0]?.providerName || 'Current Carrier'),
      Service:        draftCombined.monthlyServiceTotal,
      Hardware:       draftCombined.monthlyHardwareTotal,
      'Taxes & Fees': draftCombined.taxesAndFees,
    },
    {
      name: 'Proposed Solution',
      Service:        nxtServiceMrc,
      Hardware:       nxtDeviceMrc,
      'Taxes & Fees': nxtMrc * 0.07,
    },
  ];

  const lineData = Array.from({ length: quote.term }, (_, m) => ({
    month: `Mo ${m + 1}`,
    Current:   currTotal * (m + 1),
    Proposed:  nxtMrc * (m + 1) + draftCombined.etfTotal + nxtDeviceOneTime,
  }));

  const saveCarrierEdit = (index: number, updatedCarrier: CarrierBreakdown) => {
    const newCarriers = [...draftCarriers];
    newCarriers[index] = updatedCarrier;
    setDraftCarriers(newCarriers);
    
    // Recalculate combined for the global update
    const newCombined = newCarriers.reduce((acc, c) => {
      if (c.failed) return acc;
      return {
        monthlyServiceTotal: acc.monthlyServiceTotal + c.monthlyServiceTotal,
        monthlyHardwareTotal: acc.monthlyHardwareTotal + c.monthlyHardwareTotal,
        taxesAndFees: acc.taxesAndFees + c.taxesAndFees,
        etfTotal: acc.etfTotal + c.etfTotal,
        totalDue: acc.totalDue + c.totalDue,
        deviceCounts: {
          phone: acc.deviceCounts.phone + c.deviceCounts.phone,
          tablet: acc.deviceCounts.tablet + c.deviceCounts.tablet,
          watch: acc.deviceCounts.watch + c.deviceCounts.watch,
          connected: acc.deviceCounts.connected + c.deviceCounts.connected,
          other: acc.deviceCounts.other + c.deviceCounts.other,
        }
      };
    }, {
      monthlyServiceTotal: 0, monthlyHardwareTotal: 0, taxesAndFees: 0, etfTotal: 0, totalDue: 0,
      deviceCounts: { phone: 0, tablet: 0, watch: 0, connected: 0, other: 0 }
    });

    onUpdateCurrentSolution({ carriers: newCarriers, combined: newCombined });
    setEditingIndex(null);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Combined Summary Card at Top */}
      <div className="p-6 rounded-2xl bg-gradient-to-br from-purple-900/40 via-violet-900/20 to-[#0D0D1A] border border-purple-500/20">
        <h2 className="font-black text-white text-lg flex items-center gap-2 mb-4">
          <Building2 size={20} className="text-purple-400" />
          Combined Current Solution
        </h2>
        
        <div className="grid md:grid-cols-3 gap-6">
          <div className="space-y-1">
            <div className="text-xs text-gray-500 uppercase tracking-wider">Total Monthly Spend</div>
            <div className="text-3xl font-black text-white">${currTotal.toFixed(2)}</div>
            <div className="text-xs text-gray-400 mt-1">Across {draftCarriers.length} carrier bill(s)</div>
          </div>
          
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Service</span>
              <span className="text-white font-medium">${draftCombined.monthlyServiceTotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Hardware</span>
              <span className="text-white font-medium">${draftCombined.monthlyHardwareTotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Taxes & Fees</span>
              <span className="text-white font-medium">${draftCombined.taxesAndFees.toFixed(2)}</span>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Phones', count: draftCombined.deviceCounts.phone, Icon: Smartphone },
              { label: 'Tablets', count: draftCombined.deviceCounts.tablet, Icon: Tablet },
              { label: 'Watches', count: draftCombined.deviceCounts.watch, Icon: Watch },
              { label: 'Connected', count: draftCombined.deviceCounts.connected, Icon: Radio },
              ...(draftCombined.deviceCounts.other > 0 ? [{ label: 'Other', count: draftCombined.deviceCounts.other, Icon: Box }] : []),
            ].map(({ label, count, Icon }) => (
              <div key={label} className="flex items-center gap-2 p-2 rounded-lg bg-white/5 border border-white/5">
                <Icon size={16} className="text-purple-400" />
                <div>
                  <div className="text-sm font-bold text-white">{count}</div>
                  <div className="text-[10px] text-gray-500 leading-none">{label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ROI tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {nxtDeviceOneTime > 0 && (
          <Tile
            label="Upfront Costs (Proposed)"
            value={`$${nxtDeviceOneTime.toFixed(2)}`}
            sub="One-time device fees"
            color="text-amber-400"
          />
        )}
        <Tile
          label="Monthly Savings"
          value={savings >= 0 ? `+$${savings.toFixed(2)}` : `-$${Math.abs(savings).toFixed(2)}`}
          sub="vs. current carrier"
          color={savings >= 0 ? 'text-emerald-400' : 'text-red-400'}
        />
        <Tile
          label="Annual Savings"
          value={`$${annualSave.toFixed(2)}`}
          sub="per year"
          color="text-emerald-400"
        />
        <Tile
          label={`${quote.term}-Mo Total Saved`}
          value={`$${termSave.toFixed(2)}`}
          sub={`Over contract term`}
          color="text-purple-400"
        />
        <Tile
          label="ETF Breakeven"
          value={roiMonths > 0 ? `${roiMonths} mo` : 'No ETF'}
          sub={draftCombined.etfTotal > 0 ? `$${draftCombined.etfTotal.toFixed(2)} ETF` : 'Switch for free'}
          color={roiMonths > 3 ? 'text-amber-400' : 'text-emerald-400'}
        />
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Bar chart */}
        <div className="p-5 rounded-2xl bg-[#0D0D1A] border border-white/8">
          <h3 className="font-bold text-white text-sm mb-4">Monthly Cost Breakdown</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={barData} barCategoryGap="30%">
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="name" tick={{ fill: '#6B7280', fontSize: 11 }} axisLine={false} />
              <YAxis tick={{ fill: '#6B7280', fontSize: 11 }} axisLine={false} tickFormatter={v => `$${v}`} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11, color: '#9CA3AF' }} />
              <Bar dataKey="Service"     fill="#7C3AED" radius={[4,4,0,0]} />
              <Bar dataKey="Hardware"    fill="#4F46E5" radius={[4,4,0,0]} />
              <Bar dataKey="Taxes & Fees" fill="#6D28D9" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Cumulative line chart */}
        <div className="p-5 rounded-2xl bg-[#0D0D1A] border border-white/8">
          <h3 className="font-bold text-white text-sm mb-4">Cumulative Cost Over {quote.term} Months</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={lineData.filter((_, i) => i % Math.ceil(quote.term / 12) === 0 || i === quote.term - 1)}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="month" tick={{ fill: '#6B7280', fontSize: 11 }} axisLine={false} />
              <YAxis tick={{ fill: '#6B7280', fontSize: 11 }} axisLine={false} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11, color: '#9CA3AF' }} />
              <Line type="monotone" dataKey="Current"   stroke="#EF4444" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="Proposed"  stroke="#7C3AED" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Individual Carrier Breakdown Cards */}
      <div className="space-y-4">
        <h3 className="font-bold text-white text-sm flex items-center gap-2 px-2">
          <FileText size={16} className="text-purple-400" />
          Carrier Bill Breakdowns
        </h3>
        
        {draftCarriers.map((carrier, index) => {
          const isEditing = editingIndex === index;
          const cardCurrTotal = carrier.totalDue;

          return (
            <div key={index} className="p-5 rounded-2xl bg-[#0D0D1A] border border-white/8 relative">
              {/* Badges */}
              {carrier.failed ? (
                <div className="mb-4 flex items-start gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-xs">
                  <XCircle size={16} className="shrink-0 mt-0.5" />
                  <div>
                    <div className="font-bold mb-1">Extraction Failed</div>
                    {carrier.errorMessage}
                  </div>
                </div>
              ) : carrier.needsReview ? (
                <div className="mb-4 flex items-start gap-2 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs">
                  <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                  <div>
                    <div className="font-bold mb-1">Manual Review Recommended</div>
                    {carrier.reviewReason}
                  </div>
                </div>
              ) : null}

              <div className="flex items-center justify-between mb-4">
                <h4 className="font-bold text-white text-base">
                  {carrier.providerName || `Carrier ${index + 1}`}
                </h4>
                <button
                  onClick={() => setEditingIndex(isEditing ? null : index)}
                  className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-500/10 border border-purple-500/20 text-purple-400 hover:bg-purple-500/20 transition-all"
                >
                  {isEditing ? 'Cancel' : <><Pencil size={12} /> Edit</>}
                </button>
              </div>

              {isEditing ? (
                <CarrierEditor 
                  initialCarrier={carrier} 
                  onSave={(updated) => saveCarrierEdit(index, updated)} 
                />
              ) : (
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {[
                      ['Service',    `$${carrier.monthlyServiceTotal.toFixed(2)}/mo`],
                      ['Hardware',   `$${carrier.monthlyHardwareTotal.toFixed(2)}/mo`],
                      ['Taxes/Fees', `$${carrier.taxesAndFees.toFixed(2)}/mo`],
                      ['ETF',        `$${carrier.etfTotal.toFixed(2)}`],
                      ['Total MRC',  `$${cardCurrTotal.toFixed(2)}/mo`],
                    ].map(([l, v]) => (
                      <div key={l} className="flex justify-between p-2 rounded-lg bg-white/3">
                        <span className="text-gray-500">{l}</span>
                        <span className="text-white font-medium">{v}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { label: 'Phones', count: carrier.deviceCounts.phone, Icon: Smartphone },
                      { label: 'Tablets', count: carrier.deviceCounts.tablet, Icon: Tablet },
                      { label: 'Watches', count: carrier.deviceCounts.watch, Icon: Watch },
                      { label: 'Connected', count: carrier.deviceCounts.connected, Icon: Radio },
                      ...(carrier.deviceCounts.other > 0 ? [{ label: 'Other', count: carrier.deviceCounts.other, Icon: Box }] : []),
                    ].map(({ label, count, Icon }) => (
                      <div key={label} className="flex items-center justify-between p-2 rounded-lg bg-white/5 border border-white/5">
                        <div className="flex items-center gap-2">
                          <Icon size={14} className="text-purple-400" />
                          <span className="text-[11px] text-gray-500">{label}</span>
                        </div>
                        <span className="text-sm font-bold text-white">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {draftCarriers.length === 0 && (
          <div className="flex items-center justify-center gap-2 p-6 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-400 text-sm">
            <AlertCircle size={16} />
            No carrier bills uploaded. Upload bills in the Builder tab to see breakdowns here.
          </div>
        )}
      </div>
    </div>
  );
};

// Sub-component for editing an individual carrier
const CarrierEditor: React.FC<{ initialCarrier: CarrierBreakdown, onSave: (c: CarrierBreakdown) => void }> = ({ initialCarrier, onSave }) => {
  const [draft, setDraft] = useState<CarrierBreakdown>(initialCarrier);
  const inputCls = 'w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 focus:border-purple-500/50 text-white text-sm outline-none transition-all';

  const handleDeviceChange = (field: keyof CarrierBreakdown['deviceCounts'], val: string) => {
    setDraft(d => ({
      ...d,
      deviceCounts: { ...d.deviceCounts, [field]: parseInt(val, 10) || 0 }
    }));
  };

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1.5 uppercase tracking-wider">Provider Name</label>
          <input type="text" value={draft.providerName} onChange={e => setDraft(d => ({ ...d, providerName: e.target.value }))} className={inputCls} />
        </div>
        {[
          ['monthlyServiceTotal',  'Monthly Service ($)'],
          ['monthlyHardwareTotal', 'Monthly Hardware ($)'],
          ['taxesAndFees',         'Taxes & Fees ($)'],
          ['priorPeriodAdjustment','Prior Period Adj ($)'],
          ['otherOneTimeCredits',  'One-Time Credits ($)'],
          ['etfTotal',             'ETF Total ($)'],
        ].map(([key, label]) => (
          <div key={key}>
            <label className="block text-xs text-gray-500 mb-1.5 uppercase tracking-wider">{label}</label>
            <input
              type="number"
              value={draft[key as keyof CarrierBreakdown] as number}
              onChange={e => setDraft(d => ({ ...d, [key as string]: parseFloat(e.target.value) || 0 }))}
              className={inputCls}
            />
          </div>
        ))}
      </div>
      
      <div className="border-t border-white/10 pt-4 mt-2">
        <label className="block text-xs text-gray-500 mb-3 uppercase tracking-wider">Device Counts</label>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[
            ['phone', 'Phones'], ['tablet', 'Tablets'], ['watch', 'Watches'], ['connected', 'Connected Devs'], ['other', 'Other']
          ].map(([key, label]) => (
            <div key={key}>
              <label className="block text-[10px] text-gray-500 mb-1">{label}</label>
              <input
                type="number"
                value={draft.deviceCounts[key as keyof CarrierBreakdown['deviceCounts']]}
                onChange={e => handleDeviceChange(key as keyof CarrierBreakdown['deviceCounts'], e.target.value)}
                className={inputCls}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end pt-2">
        <button onClick={() => {
          const recalculatedTotalDue = draft.monthlyServiceTotal + draft.monthlyHardwareTotal + draft.taxesAndFees - draft.priorPeriodAdjustment - draft.otherOneTimeCredits;
          onSave({ ...draft, totalDue: recalculatedTotalDue });
        }} className="px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-sm font-bold transition-all">
          Save Changes
        </button>
      </div>
    </div>
  );
};
